import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { FetchUserDetails } from "../../../hooks/APIHooks";

function UserDetails() {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const API_KEY = process.env.REACT_APP_API_KEY;
        const response = await fetch(`${FetchUserDetails}?user_id=${userId}`, {
          headers: {
            "Authorization": API_KEY
          }
        });
        const result = await response.json();

        if (result.status === "success") {
          setData(result.data.user_details);
          setOrders(result.data.orders || []);
          setSessions(result.data.sessions || []);
        } else {
          setError(result.message);
        }
      } catch (err) {
        setError("Failed to fetch user details.");
      } finally {
        setLoading(false);
      }
    };

    fetchUserDetails();
  }, [userId]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-800 mb-2">User Details</h1>
      <p className="text-gray-600 mb-6">User ID: {userId}</p>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Info Card */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">User Information</h2>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-600">User ID</label>
                <p className="text-gray-900">{data?.user_id}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Name</label>
                <p className="text-gray-900">{data?.name}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Email</label>
                <p className="text-gray-900 break-all">{data?.emailaddress}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Phone</label>
                <p className="text-gray-900">{data?.phonenumber}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Role</label>
                <p className="text-gray-900 capitalize">{data?.role}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Gender</label>
                <p className="text-gray-900">{data?.sex}</p>
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium text-gray-600">Address</label>
                <p className="text-gray-900">{data?.address}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Verified</label>
                <p className={`font-medium ${data?.is_active ? 'text-green-600' : 'text-red-600'}`}>
                  {data?.is_active ? "Yes" : "No"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Created</label>
                <p className="text-gray-900 text-sm">{new Date(data?.created_at).toLocaleDateString()}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Session Info */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Sessions ({sessions.length})</h2>
          {sessions.length > 0 ? (
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {sessions.map((session, index) => (
                <div key={index} className="border-b border-gray-100 pb-3 last:border-b-0">
                  <div className="text-sm space-y-1">
                    <p><span className="font-medium">IP:</span> {session.ip_address}</p>
                    <p><span className="font-medium">Last Activity:</span> {new Date(session.last_activity * 1000).toLocaleString()}</p>
                    <p className="text-xs text-gray-500 truncate">
                      <span className="font-medium">Device:</span> {session.user_agent}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No sessions found</p>
          )}
        </div>
      </div>

      {/* Orders Info */}
      <div className="mt-6 bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Orders ({orders.length})</h2>
        {orders.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Order ID</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Total Amount</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Payment Status</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Delivery Status</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {orders.map((order) => (
                  <tr key={order.order_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">#{order.order_id}</td>
                    <td className="px-4 py-3 text-sm">₹{order.total_amount}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        order.payment_status === 'Completed' 
                          ? 'bg-green-100 text-green-800'
                          : order.payment_status === 'Pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {order.payment_status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        order.delivery_status === 'Delivered' 
                          ? 'bg-green-100 text-green-800'
                          : order.delivery_status === 'Processing'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {order.delivery_status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(order.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">No orders found</p>
        )}
      </div>
    </div>
  );
}

export default UserDetails;